import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Card } from '@components/common/UIComponents';
import {
  getAllUserData,
  getUnlockedAchievements,
  unlockMultipleAchievements
} from '@services/firestoreService';
import {
  ACHIEVEMENTS,
  calculateAchievementStats,
  checkAllAchievements,
  getAchievementById
} from '@utils/achievements';
import {
  Activity,
  Heart,
  Droplets,
  Utensils,
  TrendingUp,
  Flame,
  Award,
  ChevronRight,
  Calendar,
  Target,
  Zap,
  Moon,
  Weight as WeightIcon,
  BookOpen,
  Trophy
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { format, subDays, startOfWeek, endOfWeek } from 'date-fns';

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState(null);
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [newAchievements, setNewAchievements] = useState([]);
  const [showAchievementModal, setShowAchievementModal] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, [user]);

  const loadDashboardData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Load all data for the last 30 days
      const data = await getAllUserData(user.uid, 30);
      setAllData(data);
      
      // Load unlocked achievements
      const unlocked = await getUnlockedAchievements(user.uid);
      setUnlockedAchievements(unlocked);
      
      // Check for new achievements
      const stats = calculateAchievementStats(data);
      const newly = checkAllAchievements(stats, unlocked);
      
      if (newly.length > 0) {
        setNewAchievements(newly);
        setShowAchievementModal(true);
        
        // Unlock them in Firestore
        await unlockMultipleAchievements(user.uid, newly.map(a => a.id));
        setUnlockedAchievements([...unlocked, ...newly.map(a => a.id)]);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate today's stats
  const todayStats = useMemo(() => {
    if (!allData) return null;

    const today = format(new Date(), 'yyyy-MM-dd');
    
    const todaySteps = allData.steps.find(s => s.dateId === today)?.steps || 0;
    const todayHeartPoints = allData.heartPoints.find(h => h.dateId === today)?.points || 0;
    const todayWater = allData.water
      .filter(w => w.dateId === today)
      .reduce((sum, w) => sum + w.amount, 0);
    const todayCalories = allData.meals
      .filter(m => m.dateId === today)
      .reduce((sum, m) => sum + m.calories, 0);
    const todaySleep = allData.sleep.find(s => s.dateId === today)?.duration || 0;

    return {
      steps: todaySteps,
      heartPoints: todayHeartPoints,
      water: todayWater,
      calories: todayCalories,
      sleep: todaySleep
    };
  }, [allData]);

  // Calculate goals
  const goals = useMemo(() => {
    if (!allData) return null;

    const settings = allData.userSettings;
    return {
      steps: settings.stepsGoal || 8000,
      heartPoints: settings.heartPointsGoal || 20,
      water: settings.waterGoal || 2000,
      calories: settings.dailyCalorieGoal || 2000,
      sleep: settings.sleepGoal || 8
    };
  }, [allData]);

  // Calculate streak
  const streak = useMemo(() => {
    if (!allData) return 0;

    const allDates = new Set();
    const { steps, water, sleep, journal, meals } = allData;
    
    [...steps, ...water, ...sleep, ...journal, ...meals].forEach(entry => {
      if (entry.dateId) allDates.add(entry.dateId);
    });
    
    const sortedDates = Array.from(allDates).sort().reverse();
    let currentStreak = 0;
    const today = new Date();
    
    for (let i = 0; i < sortedDates.length; i++) {
      const expectedDate = new Date(today);
      expectedDate.setDate(today.getDate() - i);
      const expectedDateId = format(expectedDate, 'yyyy-MM-dd');
      
      if (sortedDates[i] === expectedDateId) {
        currentStreak++;
      } else {
        break;
      }
    }
    
    return currentStreak;
  }, [allData]);

  // Calculate weekly summary
  const weeklySummary = useMemo(() => {
    if (!allData) return null;

    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });
    const weekStartId = format(weekStart, 'yyyy-MM-dd');
    const weekEndId = format(weekEnd, 'yyyy-MM-dd');

    const weekSteps = allData.steps
      .filter(s => s.dateId >= weekStartId && s.dateId <= weekEndId)
      .reduce((sum, s) => sum + (s.steps || 0), 0);

    const weekWater = allData.water
      .filter(w => w.dateId >= weekStartId && w.dateId <= weekEndId)
      .reduce((sum, w) => sum + w.amount, 0);

    const weekCalories = allData.meals
      .filter(m => m.dateId >= weekStartId && m.dateId <= weekEndId)
      .reduce((sum, m) => sum + m.calories, 0);

    const weekSleep = allData.sleep
      .filter(s => s.dateId >= weekStartId && s.dateId <= weekEndId)
      .reduce((sum, s) => sum + (s.duration || 0), 0);

    const weekJournals = allData.journal
      .filter(j => j.dateId >= weekStartId && j.dateId <= weekEndId).length;

    return {
      steps: weekSteps,
      avgSteps: Math.round(weekSteps / 7),
      water: weekWater,
      avgWater: Math.round(weekWater / 7),
      calories: weekCalories,
      avgCalories: Math.round(weekCalories / 7),
      sleep: weekSleep,
      avgSleep: (weekSleep / 7).toFixed(1),
      journals: weekJournals
    };
  }, [allData]);

  // Recent achievements (last 3)
  const recentAchievements = useMemo(() => {
    return unlockedAchievements.slice(-3).reverse().map(id => getAchievementById(id)).filter(Boolean);
  }, [unlockedAchievements]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!todayStats || !goals) {
    return <div>Error loading data</div>;
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Welcome back! 👋
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {format(new Date(), 'EEEE, MMMM d, yyyy')}
        </p>
      </div>

      {/* Streak Card */}
      {streak > 0 && (
        <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Flame size={32} />
                <span className="text-4xl font-bold">{streak}</span>
              </div>
              <p className="text-white/90 text-lg">Day Streak!</p>
              <p className="text-white/75 text-sm mt-1">Keep it up! You're on fire! 🔥</p>
            </div>
            <Trophy size={64} className="opacity-20" />
          </div>
        </Card>
      )}

      {/* Today's Progress - Goal Rings */}
      <Card>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Today's Progress
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          <GoalRing
            value={todayStats.steps}
            goal={goals.steps}
            icon={Activity}
            label="Steps"
            color="text-blue-600"
            bgColor="bg-blue-100"
          />
          <GoalRing
            value={todayStats.heartPoints}
            goal={goals.heartPoints}
            icon={Heart}
            label="Heart Pts"
            color="text-red-600"
            bgColor="bg-red-100"
          />
          <GoalRing
            value={todayStats.water}
            goal={goals.water}
            icon={Droplets}
            label="Water (ml)"
            color="text-cyan-600"
            bgColor="bg-cyan-100"
          />
          <GoalRing
            value={todayStats.calories}
            goal={goals.calories}
            icon={Utensils}
            label="Calories"
            color="text-orange-600"
            bgColor="bg-orange-100"
          />
          <GoalRing
            value={todayStats.sleep}
            goal={goals.sleep}
            icon={Moon}
            label="Sleep (h)"
            color="text-purple-600"
            bgColor="bg-purple-100"
          />
        </div>
      </Card>

      {/* Weekly Summary */}
      {weeklySummary && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              This Week's Summary
            </h2>
            <Calendar size={20} className="text-gray-400" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <SummaryCard
              icon={Activity}
              label="Avg Steps"
              value={weeklySummary.avgSteps.toLocaleString()}
              color="text-blue-600"
              bgColor="bg-blue-50 dark:bg-blue-900/20"
            />
            <SummaryCard
              icon={Droplets}
              label="Avg Water"
              value={`${weeklySummary.avgWater}ml`}
              color="text-cyan-600"
              bgColor="bg-cyan-50 dark:bg-cyan-900/20"
            />
            <SummaryCard
              icon={Utensils}
              label="Avg Calories"
              value={weeklySummary.avgCalories.toLocaleString()}
              color="text-orange-600"
              bgColor="bg-orange-50 dark:bg-orange-900/20"
            />
            <SummaryCard
              icon={Moon}
              label="Avg Sleep"
              value={`${weeklySummary.avgSleep}h`}
              color="text-purple-600"
              bgColor="bg-purple-50 dark:bg-purple-900/20"
            />
            <SummaryCard
              icon={BookOpen}
              label="Journal Entries"
              value={weeklySummary.journals}
              color="text-indigo-600"
              bgColor="bg-indigo-50 dark:bg-indigo-900/20"
            />
          </div>
        </Card>
      )}

      {/* Quick Actions & Achievements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h2>
          <div className="space-y-3">
            <QuickActionLink to="/nutrition" icon={Utensils} label="Log a Meal" />
            <QuickActionLink to="/hydration" icon={Droplets} label="Add Water" />
            <QuickActionLink to="/sleep" icon={Moon} label="Log Sleep" />
            <QuickActionLink to="/journal" icon={BookOpen} label="Write Journal" />
            <QuickActionLink to="/weight" icon={WeightIcon} label="Record Weight" />
          </div>
        </Card>

        {/* Recent Achievements */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Achievements
            </h2>
            <Link 
              to="/achievements" 
              className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1"
            >
              View All
              <ChevronRight size={16} />
            </Link>
          </div>
          {recentAchievements.length > 0 ? (
            <div className="space-y-3">
              {recentAchievements.map((achievement) => (
                <AchievementCard key={achievement.id} achievement={achievement} />
              ))}
              <div className="text-center pt-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {unlockedAchievements.length} of {ACHIEVEMENTS.length} unlocked
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Trophy size={48} className="mx-auto mb-3 opacity-30" />
              <p>No achievements yet</p>
              <p className="text-sm mt-1">Keep logging to earn badges!</p>
            </div>
          )}
        </Card>
      </div>

      {/* New Achievement Modal */}
      {showAchievementModal && newAchievements.length > 0 && (
        <AchievementModal
          achievements={newAchievements}
          onClose={() => {
            setShowAchievementModal(false);
            setNewAchievements([]);
          }}
        />
      )}
    </div>
  );
};

// Goal Ring Component
const GoalRing = ({ value, goal, icon: Icon, label, color, bgColor }) => {
  const percentage = Math.min((value / goal) * 100, 100);
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-24 mb-2">
        <svg className="transform -rotate-90 w-24 h-24">
          <circle
            cx="48"
            cy="48"
            r={radius}
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-gray-200 dark:text-gray-700"
          />
          <circle
            cx="48"
            cy="48"
            r={radius}
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={color}
            strokeLinecap="round"
          />
        </svg>
        <div className={`absolute inset-0 flex items-center justify-center ${bgColor} rounded-full m-2`}>
          <Icon size={24} className={color} />
        </div>
      </div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {typeof value === 'number' && value % 1 !== 0 ? value.toFixed(1) : value} / {goal}
      </p>
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ icon: Icon, label, value, color, bgColor }) => (
  <div className={`${bgColor} rounded-lg p-4`}>
    <Icon size={20} className={`${color} mb-2`} />
    <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
    <p className="text-sm text-gray-600 dark:text-gray-400">{label}</p>
  </div>
);

// Quick Action Link Component
const QuickActionLink = ({ to, icon: Icon, label }) => (
  <Link
    to={to}
    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
  >
    <div className="flex items-center gap-3">
      <Icon size={20} className="text-primary-600 group-hover:text-primary-700" />
      <span className="font-medium text-gray-900 dark:text-white">{label}</span>
    </div>
    <ChevronRight size={20} className="text-gray-400 group-hover:text-gray-600" />
  </Link>
);

// Achievement Card Component
const AchievementCard = ({ achievement }) => {
  const Icon = achievement.icon;
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
      <div className={`${achievement.bg} p-2 rounded-lg`}>
        <Icon size={24} className={achievement.color} />
      </div>
      <div>
        <p className="font-medium text-gray-900 dark:text-white">{achievement.name}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">{achievement.description}</p>
      </div>
    </div>
  );
};

// Achievement Modal Component
const AchievementModal = ({ achievements, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 animate-bounce-in">
      <div className="text-center">
        <div className="mb-4">
          <Trophy size={64} className="mx-auto text-yellow-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Achievement Unlocked! 🎉
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          You've earned {achievements.length} new {achievements.length === 1 ? 'badge' : 'badges'}!
        </p>
        <div className="space-y-3 mb-6">
          {achievements.map((achievement) => (
            <AchievementCard key={achievement.id} achievement={achievement} />
          ))}
        </div>
        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Awesome!
        </button>
      </div>
    </div>
  </div>
);

export default Dashboard;
